import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Scale } from 'lucide-react';
import { formatGodziny, klasaLimitu, klasaPaska } from '../utils/nadgodziny';

/**
 * Panel limitów Kodeksu pracy dla wniosku: roczny (150 h) i tygodniowy (8 h).
 * `stan` pochodzi z GET /nadgodziny/limity albo /nadgodziny/:id/limity.
 * `tryb`: 'skladanie' (pracownik — liczy oczekujące) albo 'decyzja' (przełożony — liczy tylko zatwierdzone + ten wniosek).
 */
const PanelLimitowNadgodzin = ({ stan, tryb = 'skladanie', ladowanie = false, kompaktowy = false }) => {
  if (ladowanie && !stan) {
    return <div className="rounded-xl border border-gray-200 bg-surface px-4 py-3 text-sm text-ink-faint">Sprawdzanie limitów nadgodzin…</div>;
  }
  if (!stan) return null;

  const { rok, tydzien } = stan;
  const decyzja = tryb === 'decyzja';
  const razem = decyzja ? rok.zatwierdzoneZWnioskiem : rok.razemZWnioskiem;
  const przekroczony = decyzja ? rok.przekroczonyPoZatwierdzeniu : rok.przekroczony;
  const procent = rok.limit > 0 ? Math.round((razem / rok.limit) * 100) : 0;
  const stanKoloru = { przekroczony, ostrzezenie: procent >= rok.prog };
  const Ikona = przekroczony ? AlertCircle : stanKoloru.ostrzezenie ? AlertTriangle : CheckCircle;

  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${klasaLimitu(stanKoloru)}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-medium flex items-center gap-1.5">
          <Scale className="h-4 w-4 shrink-0" />
          Limit roczny nadgodzin {rok.rok}: {formatGodziny(razem)} z {formatGodziny(rok.limit)} ({procent}%)
        </p>
        <Ikona className="h-4 w-4 shrink-0 mt-0.5" />
      </div>
      <div className="mt-2 h-2 w-full rounded-full bg-black/10 overflow-hidden">
        <div className={`h-full rounded-full ${klasaPaska(procent)}`} style={{ width: `${Math.min(100, procent)}%` }} />
      </div>
      {!kompaktowy && (
        <p className="mt-2 text-xs opacity-90">
          Zatwierdzone w roku: <strong>{formatGodziny(rok.zatwierdzone)}</strong>
          {' · '}oczekujące na decyzję: <strong>{formatGodziny(rok.oczekujace)}</strong>
          {' · '}ten wniosek: <strong>{formatGodziny(rok.godzinyWniosku)}</strong>
          {decyzja && rok.oczekujace > 0 && <span className="block mt-0.5">Do blokady zgody liczą się tylko zatwierdzone godziny i ten wniosek; oczekujące pokazano informacyjnie.</span>}
        </p>
      )}
      {przekroczony && (
        <p className="mt-2 text-xs font-medium flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {decyzja
            ? 'Zgoda przekroczyłaby roczny limit 150 godzin nadliczbowych (art. 151 § 3 KP).'
            : 'Ten wniosek przekracza roczny limit nadgodzin — nie można go złożyć.'}
        </p>
      )}
      {!przekroczony && stanKoloru.ostrzezenie && (
        <p className="mt-2 text-xs flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />Wykorzystano co najmniej {rok.prog}% rocznego limitu — planuj pozostałe nadgodziny ostrożnie.
        </p>
      )}
      {tydzien?.przekroczony && (
        <p className={`mt-2 text-xs flex items-center gap-1 ${przekroczony ? '' : 'text-amber-900'}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Tydzień {tydzien.tydzien}: {formatGodziny(tydzien.suma)} nadgodzin — powyżej {formatGodziny(tydzien.limit)} (średnia 48 godz. tygodniowo, art. 131 KP).
        </p>
      )}
    </div>
  );
};

export default PanelLimitowNadgodzin;
